import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api.js';
import Icon from './Icon.jsx';

// Ask-analyst ACTIVITY for one client — how much the analyst is used, by whom,
// and what people start from (server/analystUsage.js).
//
// Dual-surface via the `scope` prop, asymmetric on purpose:
//   'admin-client' (Admin → the client → Analyst activity) — LINK the client to
//                  an Inventive workspace, then read the report.
//   'my'           (Settings → Analyst activity)              — READ their own.
// Which workspace a Pulse client maps to is a Howler decision, not a client one:
// picking it wrong would show one organiser another's questions. So the picker is
// staff-only and the client surface has no way to change the link.
//
// WHAT WAS ASKED is its own switch (`ai.analyst_questions`, off by default). With
// it on, Recent conversations carries the opening message in the asker's own words
// — redacted server-side, because the vendor's reference warns these titles carry
// pasted guest lists — and a link into the analyst, where the full thread lives.
// Pulse cannot show the transcript itself: the reporting API has no answer text in
// it at all, only that one free-text field. With the switch off the list still
// shows (who, when, how many answers, the link) minus the wording, and the panel
// says which it is — "switched off" and "nobody asked anything" must not look
// alike.
//
// Mobile-first: one stacked column at every width, ≥40px controls, tables scroll
// inside their own box rather than widening the page.
const WINDOWS = [[7, '7 days'], [30, '30 days'], [90, '90 days'], [365, '12 months']];

const n = (v) => Number(v || 0).toLocaleString();
const shortDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return String(s); }
};
// Date + time of day — inside one conversation the ORDER and spacing of answers is
// the point, and a bare date would print the same string five times.
const stampTime = (v) => {
  if (!v) return '';
  try { return new Date(v).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return String(v); }
};
const monthLabel = (m) => {
  try { return new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' }); } catch { return m; }
};

export default function AnalystUsage({ entityId, scope = 'my', heading = true }) {
  const isAdmin = scope === 'admin-client';
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);

  const load = useCallback((opts = {}) => {
    setLoading(true); setError('');
    const p = isAdmin ? api.adminAnalystUsage(entityId, { days, ...opts }) : api.myAnalystUsage(entityId, { days });
    return p
      .then((v) => { setData(v); setDenied(false); })
      .catch((e) => { if (e?.status === 403) setDenied(true); else setError(e?.message || 'Could not load analyst activity.'); })
      .finally(() => setLoading(false));
  }, [entityId, days, isAdmin]);

  useEffect(() => { let alive = true; load().then(() => { if (!alive) return; }); return () => { alive = false; }; }, [load]);

  // A client whose switch is off simply doesn't see the section (the route 403s).
  if (denied) return null;

  return (
    <div>
      <div style={headerRow}>
        {/* On its own page the title bar above already says this; inside the admin
            client tab this IS the heading. */}
        {heading && <h3 style={h3}><Icon name="owl" size={16} /> Owl Analyst Activity</h3>}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {WINDOWS.map(([d, label]) => (
            <button key={d} onClick={() => setDays(d)} style={{ ...chip, ...(days === d ? chipOn : null) }}>{label}</button>
          ))}
        </div>
      </div>
      <p style={hint}>
        How <b>{data?.entity?.name || 'this client'}</b>’s team is using the AI analyst — how many questions were asked, who
        asked them, and what they started from. {isAdmin ? '' : 'Read live from the analyst itself. '}
        Open any conversation to read the full thread in the analyst.
      </p>

      {isAdmin && <WorkspaceLink entityId={entityId} link={data?.link || null} onChange={() => load({ fresh: true })} />}

      {loading && !data && <div style={box}>Loading…</div>}
      {error && <div style={{ ...box, color: 'var(--danger,#dc2626)' }}>{error}</div>}

      {data && !data.ready && (
        <div style={box}>
          <b>Nothing to report yet.</b>
          <p style={{ ...hint, margin: '6px 0 0' }}>{data.message}</p>
        </div>
      )}

      {data && data.ready && <Report data={data} isAdmin={isAdmin} entityId={entityId} onRefresh={() => load({ fresh: true })} busy={loading} />}
    </div>
  );
}

// ── Admin only: which Inventive workspace is this client? ────────────────────
export function WorkspaceLink({ entityId, link, onChange }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');

  const openPicker = () => {
    setOpen(true); setErr('');
    if (rows) return;
    api.analystWorkspaces(entityId).then((v) => setRows(v.workspaces || [])).catch((e) => setErr(e?.message || 'Could not list analyst workspaces.'));
  };
  const save = (ws) => {
    setBusy(true); setErr('');
    api.linkAnalystWorkspace(entityId, ws ? { workspaceId: ws.id, workspaceName: ws.name } : { workspaceId: '' })
      .then(() => { setOpen(false); onChange(); })
      .catch((e) => setErr(e?.message || 'Could not save the link.'))
      .finally(() => setBusy(false));
  };

  const term = q.trim().toLowerCase();
  const shown = (rows || []).filter((r) => !term || r.name.toLowerCase().includes(term));

  return (
    <div style={box}>
      <div style={lbl}>Analyst workspace</div>
      {link ? (
        <p style={{ margin: '6px 0 0', fontSize: 13 }}>
          Linked to <b>{link.workspaceName || link.workspaceId}</b>
          {link.linkedBy ? <span style={{ color: 'var(--muted)' }}> · by {link.linkedBy}</span> : null}
        </p>
      ) : (
        <p style={{ ...hint, margin: '6px 0 0' }}>
          Not linked yet. Over at Inventive a client is a <b>workspace</b>; nothing in their reporting data carries a Pulse id,
          so the two are joined here once, by hand. Until that is done there is nothing to report.
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button style={mini} onClick={openPicker}>{link ? 'Change workspace' : 'Link a workspace'}</button>
        {link && <button style={dangerMini} disabled={busy} onClick={() => save(null)}>Unlink</button>}
      </div>
      {err && <p style={{ ...hint, color: 'var(--danger,#dc2626)' }}>{err}</p>}

      {open && (
        <div style={{ marginTop: 12 }}>
          <input style={input} placeholder="Search workspaces…" value={q} onChange={(e) => setQ(e.target.value)} />
          {!rows && <p style={hint}>Loading workspaces…</p>}
          {rows && !shown.length && <p style={hint}>No workspace matches “{q}”.</p>}
          <div style={{ maxHeight: 340, overflowY: 'auto', marginTop: 8 }}>
            {shown.map((w) => (
              <div key={w.id} style={{ ...pickRow, ...(w.suggested ? { borderColor: 'var(--brand)', background: 'var(--brand-tint,#fff0f3)' } : null) }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, wordBreak: 'break-word' }}>
                    {w.name || '(unnamed)'}
                    {w.suggested && <span style={tag}>name matches</span>}
                    {w.isTest && <span style={{ ...tag, background: '#6b7280' }}>looks like a test</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                    {n(w.responses)} answers · {n(w.users)} people · last active {shortDate(w.lastActive)}
                    {w.claimedBy && <> · <b>already linked to {w.claimedBy.name}</b></>}
                  </div>
                </div>
                <button style={mini} disabled={busy || !!w.claimedBy} onClick={() => save(w)}>
                  {w.claimedBy ? 'Taken' : 'Link'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── The report ───────────────────────────────────────────────────────────────
// (Report/DailyChart/MonthlyChart/WorkspaceLink are exported for the render test:
//  a static render never runs effects, so the top-level component would only ever
//  be exercised in its loading state.)
export function Report({ data, isAdmin, onRefresh, busy, entityId }) {
  const t = data.totals || {};
  const life = data.lifetime || {};
  const users = data.users || [];
  const internal = users.filter((u) => u.senderType === 'Internal user');
  const external = users.filter((u) => u.senderType !== 'Internal user');

  return (
    <>
      <div style={tiles}>
        <Tile label="Questions answered" value={n(t.responses)} sub={`in the last ${data.window?.days} days`} />
        <Tile label="Conversations" value={n(t.threads)} sub="separate threads" />
        <Tile label="People asking" value={n(t.users)} sub={`${n(t.externalUsers)} of them organisers`} />
        <Tile
          label="Feedback"
          value={t.rated ? `${n(t.thumbsUp)} up / ${n(t.thumbsDown)} down` : 'None left'}
          sub={t.responses ? `${t.ratedPct}% of answers rated` : ''}
        />
      </div>

      <div style={box}>
        <div style={lbl}>All time</div>
        <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.6 }}>
          {n(life.responses)} answers across {n(life.threads)} conversations from {n(life.users)} people.
          {life.firstAt ? <> First question {shortDate(life.firstAt)}; most recent {shortDate(life.lastAt)}.</> : null}
          {life.lastExternalAt
            ? <> An organiser last used it {shortDate(life.lastExternalAt)}.</>
            : <> <b>No organiser has ever used it</b> — all the activity above is their own staff.</>}
        </p>
      </div>

      <DailyChart series={data.series || []} days={data.window?.days} />
      <MonthlyChart months={data.months || []} />

      <div style={box}>
        <div style={lbl}>Who is asking</div>
        {!users.length && <p style={hint}>Nobody asked anything in this window.</p>}
        {!!users.length && (
          <>
            <p style={hint}>
              {n(internal.length)} of the client’s own staff and {n(external.length)} organisers.
              “Lifetime” counts every question that person has ever asked <b>for this client</b>, all the way back — it ignores
              the window above, but not the client: work they do for anyone else is not in it.
            </p>
            <div style={scrollX}>
              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}>Person</th><th style={th}>Type</th><th style={thNum}>Questions</th>
                    <th style={thNum}>Threads</th><th style={thNum}>Lifetime</th><th style={th}>Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.email || u.name}>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{u.name || u.email || '—'}</div>
                        {u.name && u.email && <div style={{ fontSize: 11.5, color: 'var(--muted)', wordBreak: 'break-all' }}>{u.email}</div>}
                      </td>
                      <td style={td}>{u.senderType === 'Internal user' ? 'Staff' : 'Organiser'}{u.tier ? <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{u.tier}</div> : null}</td>
                      <td style={tdNum}>{n(u.responses)}</td>
                      <td style={tdNum}>{n(u.threads)}</td>
                      <td style={tdNum}>{n(u.lifetimeResponses)}</td>
                      <td style={td}>{shortDate(u.lastActive)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <Conversations data={data} entityId={entityId} isAdmin={isAdmin} />

      <div style={box}>
        <div style={lbl}>What they start from</div>
        {!(data.topics || []).length ? (
          <p style={hint}>
            Nobody started from a saved prompt in this window — every conversation was typed from scratch.
            Only threads begun from a canned prompt carry a topic, so there is nothing to group by.
          </p>
        ) : (
          <>
            <p style={hint}>
              {n(data.topicCoverage?.seededThreads)} of {n(data.topicCoverage?.totalThreads)} conversations started from a saved prompt.
              The rest were typed from scratch and carry no topic, so this classifies that slice only — it is a
              prompt-adoption read, not a breakdown of everything asked.
            </p>
            <div style={scrollX}>
              <table style={table}>
                <thead><tr><th style={th}>Topic</th><th style={th}>Prompt</th><th style={thNum}>Threads</th><th style={thNum}>Answers</th></tr></thead>
                <tbody>
                  {data.topics.map((r, i) => (
                    <tr key={`${r.topic}|${r.prompt}|${i}`}>
                      <td style={td}>{r.topic || '—'}</td><td style={td}>{r.prompt || '—'}</td>
                      <td style={tdNum}>{n(r.threads)}</td><td style={tdNum}>{n(r.responses)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div style={box}>
        <div style={lbl}>Which data it had to query</div>
        {!(data.explores || []).length ? (
          <p style={hint}>No answer in this window needed a query against the client’s own data.</p>
        ) : (
          <>
            <div style={scrollX}>
              <table style={table}>
                <thead><tr><th style={th}>Model</th><th style={th}>Explore</th><th style={thNum}>Queries run</th><th style={thNum}>Answers using it</th></tr></thead>
                <tbody>
                  {data.explores.map((r, i) => (
                    <tr key={`${r.model}|${r.explore}|${i}`}>
                      <td style={td}>{r.model || '—'}</td><td style={td}>{r.explore}</td>
                      <td style={tdNum}>{n(r.invocations)}</td><td style={tdNum}>{n(r.responses)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!!data.exploreCoverage?.unattributed && (
              <p style={hint}>
                A further {n(data.exploreCoverage.unattributed)} of {n(data.exploreCoverage.total)} queries could not be traced back
                to a named explore, so this table is a partial view. Counted here rather than dropped, so the totals still add up.
              </p>
            )}
          </>
        )}
      </div>

      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
          <button style={mini} disabled={busy} onClick={onRefresh}>{busy ? 'Refreshing…' : 'Refresh now'}</button>
          <span style={{ ...hint, margin: 0 }}>{data.cached ? 'Cached for up to 5 minutes.' : 'Just read live.'}</span>
        </div>
      )}
    </>
  );
}

// Recent conversations — the drill-through half of the report.
//
// Every row links into the analyst, where the FULL thread lives: Pulse can't render
// a transcript because the reporting API carries no answer text at all. With the
// "what was asked" switch on, each row also shows the opening message in the asker's
// own words; with it off the rows are identical minus that column, and the panel
// says so — an empty column must not read as "nobody asked anything".
export function Conversations({ data, entityId, isAdmin }) {
  const rows = data.conversations || [];
  const shown = !!data.questions?.shown;
  const [open, setOpen] = useState(null); // thread id, one at a time
  return (
    <div style={box}>
      <div style={lbl}>Recent conversations</div>
      {!rows.length ? (
        <p style={hint}>No conversations in this window.</p>
      ) : (
        <>
          <p style={hint}>
            {shown
              ? <>What each conversation was opened with, in the asker’s own words. Email addresses and phone numbers are
                  stripped automatically before anything is shown — people paste guest lists into the analyst, and those
                  are other people’s details. That cleanup is automatic and careful rather than guaranteed, so treat what
                  you read here as you would the chat itself.</>
              : <>Showing who asked and when. <b>The wording of each question is switched off</b> for this account — ask
                  Howler to turn on “what was asked” if your team should be able to read it here.</>}
            {' '}Tap a row to open it.
            {data.truncated ? ' Showing the most recent 100.' : ''}
          </p>
          <div>
            {rows.map((c) => (
              <ConversationRow
                key={c.id}
                c={c}
                shown={shown}
                entityId={entityId}
                isAdmin={isAdmin}
                open={open === c.id}
                onToggle={() => setOpen(open === c.id ? null : c.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// One conversation, expandable in place. The detail is fetched on FIRST open and
// kept — a hundred rows must not become a hundred requests on page load, and
// re-opening one you just closed should not go back to the server.
export function ConversationRow({ c, shown, entityId, isAdmin, open, onToggle }) {
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState('');
  const [retry, setRetry] = useState(0);
  // Which thread we have already started fetching. A REF, not state, and the
  // dependency array holds only things that mean "fetch something different".
  //
  // This is the second time this effect has been wrong, in opposite directions, so
  // it is worth writing down. Guarding on `busy`/`err` STATE and listing them as
  // dependencies stops the retry loop but creates a worse bug: the effect sets
  // busy, which is a dependency, so React tears down the run that is still
  // in flight — its cleanup sets alive=false — and the response is dropped when it
  // lands. The row then says "Opening…" forever. Nothing about it looks broken,
  // which is why it reached production. A ref is not a dependency, so the effect
  // runs exactly once per (open, thread) and the in-flight request is never
  // orphaned; retrying is an explicit counter bump.
  const startedRef = useRef('');
  useEffect(() => {
    if (!open || startedRef.current === c.id) return;
    startedRef.current = c.id;
    setErr('');
    let alive = true;
    const p = isAdmin ? api.adminAnalystThread(entityId, c.id) : api.myAnalystThread(entityId, c.id);
    p.then((v) => { if (alive) setDetail(v.thread || null); })
      .catch((e) => {
        if (!alive) return;
        setErr(e?.message || 'Could not open that conversation.');
        startedRef.current = ''; // let "Try again" have another go
      });
    return () => { alive = false; };
  }, [open, entityId, isAdmin, c.id, retry]);

  return (
    <div style={{ borderBottom: '1px solid var(--hairline)' }}>
      <button type="button" onClick={onToggle} style={convBtn} aria-expanded={open}>
        <span style={{ ...caret, transform: open ? 'rotate(90deg)' : 'none' }}>›</span>
        <span style={{ minWidth: 0, flex: 1 }}>
          {shown && (
            <span style={{ display: 'block', fontSize: 13, fontWeight: 600, lineHeight: 1.45, wordBreak: 'break-word' }}>
              {c.asked || <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(opened without a message)</span>}
            </span>
          )}
          <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', marginTop: shown ? 4 : 0 }}>
            {c.name || c.email || 'Unknown'} · {c.senderType === 'Internal user' ? 'Staff' : 'Organiser'} · {shortDate(c.at)} · {n(c.answers)} {c.answers === 1 ? 'answer' : 'answers'}
            {c.deleted ? ' · deleted' : ''}
          </span>
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 0 12px 22px' }}>

          {err && (
            <p style={{ ...hint, color: 'var(--danger,#dc2626)' }}>
              {err}{' '}
              <button type="button" onClick={() => { setErr(''); setRetry((r) => r + 1); }} style={retryBtn}>Try again</button>
            </p>
          )}
          {/* Rendered from the row we ALREADY have, so opening one shows its header
              at once and only the answer list waits on the vendor. A panel that
              opens to the word "Opening…" reads as broken the moment the request
              takes more than an instant — and it always takes more than an instant. */}
          {!err && <ThreadDetail t={detail} row={c} shown={shown} loading={!detail} />}
        </div>
      )}
    </div>
  );
}

// The expanded conversation.
//
// This is NOT a transcript, and says so rather than looking half-built: the
// reporting feed Pulse reads carries no message text at all — the opening message
// is the only wording in it — so what can honestly be shown is the SHAPE of the
// exchange: every answer in order, when it landed, how it was rated, whether it
// finished, and which of the client’s own explores it had to query to answer.
// That is genuinely what most people open a conversation to find out. Reading the
// words themselves still means opening it in the analyst, which is one tap away.
export function ThreadDetail({ t, row, shown, loading }) {
  // `t` once the fetch lands; `row` is the summary the list already had. Everything
  // in the header exists in both, so the panel never opens empty.
  const head = t || row || {};
  const answers = (t && t.answers) || [];
  return (
    <div style={threadBox}>
      {shown && head.asked ? (
        <div style={{ marginBottom: 10 }}>
          <div style={lbl}>Opened with</div>
          <div style={{ fontSize: 13, lineHeight: 1.5, marginTop: 3, wordBreak: 'break-word' }}>{head.asked}</div>
        </div>
      ) : null}
      {(head.topic || head.prompt) && (
        <p style={{ ...hint, marginTop: 0 }}>Started from <b>{head.prompt || head.topic}</b>{head.prompt && head.topic ? ` · ${head.topic}` : ''}.</p>
      )}

      <div style={lbl}>
        {loading
          ? `${n(head.answers || 0)} ${head.answers === 1 ? 'answer' : 'answers'}`
          : `${n(answers.length)} ${answers.length === 1 ? 'answer' : 'answers'}`}
      </div>
      {loading && <p style={hint}>Reading the conversation…</p>}
      {!loading && !answers.length && <p style={hint}>No answer was recorded for this conversation.</p>}
      <ol style={{ margin: '6px 0 0', padding: 0, listStyle: 'none' }}>
        {answers.map((a, i) => (
          <li key={a.id} style={step}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <b style={{ fontSize: 12.5 }}>Answer {i + 1}</b>
              <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{stampTime(a.at)}</span>
              {a.rating === 'THUMBS_UP' && <span style={{ ...pill, background: 'var(--success,#10b981)' }}>rated up</span>}
              {a.rating === 'THUMBS_DOWN' && <span style={{ ...pill, background: 'var(--danger,#dc2626)' }}>rated down</span>}
              {!a.completed && <span style={{ ...pill, background: '#6b7280' }}>did not finish</span>}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>
              {a.explores?.length
                ? <>Queried {a.explores.join(', ')}{a.queries > 1 ? ` · ${n(a.queries)} queries` : ''}</>
                : 'Answered without querying the data'}
            </div>
            {a.url && <a href={a.url} target="_blank" rel="noopener noreferrer" style={stepLink}>Read this answer ↗</a>}
          </li>
        ))}
      </ol>

      <p style={{ ...hint, borderTop: '1px solid var(--hairline)', marginTop: 10, paddingTop: 8 }}>
        The words of each answer aren’t in the reporting feed Pulse reads — only the opening message is — so the wording
        lives in the analyst.{' '}
        {head.url
          ? <a href={head.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand)', fontWeight: 600 }}>Open the whole conversation ↗</a>
          : <>This conversation was deleted, so it no longer opens.</>}
      </p>
    </div>
  );
}

// Daily bars. A quiet day is a real zero here — the server fills the gaps the
// reporting API leaves absent, so a sparse client shows gaps instead of a line
// that invents activity between two live days.
export function DailyChart({ series, days }) {
  const max = Math.max(1, ...series.map((d) => d.responses));
  const active = series.filter((d) => d.responses > 0).length;
  // A 12-month window is 365 bars. At a fixed 2px gap the gaps ALONE are 730px, so
  // the row would scroll a phone sideways. Past ~120 points the bars go gapless and
  // fully shrinkable, which keeps every window inside the card at any width.
  const dense = series.length > 120;
  const gap = dense ? 0 : 2;
  const minWidth = dense ? 0 : 2;
  return (
    <div style={box}>
      <div style={lbl}>Day by day</div>
      <p style={hint}>Questions answered per day. Used on {n(active)} of the last {n(days)} days.</p>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap, height: 90, marginTop: 8, overflow: 'hidden' }}>
        {series.map((d) => (
          <div
            key={d.date}
            title={`${d.date}: ${d.responses} answers`}
            style={{
              flex: 1, minWidth, borderRadius: dense ? 0 : '2px 2px 0 0',
              height: `${Math.max(d.responses ? 4 : 1, Math.round((d.responses / max) * 100))}%`,
              background: d.responses ? 'var(--brand)' : 'var(--hairline)',
            }}
          />
        ))}
      </div>
      <div style={axis}><span>{series[0]?.date || ''}</span><span>{series[series.length - 1]?.date || ''}</span></div>
    </div>
  );
}

// Twelve months, split by who asked. The story worth seeing is the client's own
// staff handing over to their organisers, so the two are stacked, not summed.
export function MonthlyChart({ months }) {
  if (!months.length) return null;
  const max = Math.max(1, ...months.map((m) => m.internal + m.external));
  return (
    <div style={box}>
      <div style={lbl}>Twelve months</div>
      <p style={hint}>
        <span style={key1} /> the client’s own staff · <span style={key2} /> their organisers. Always the last twelve months,
        whatever window is picked above.
      </p>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120, marginTop: 8, overflow: 'hidden' }}>
        {months.map((m) => (
          <div key={m.month} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}
            title={`${m.month}: ${m.internal} from staff, ${m.external} from organisers`}>
            <div style={{ height: `${Math.round((m.external / max) * 100)}%`, background: 'var(--brand)', borderRadius: '2px 2px 0 0' }} />
            <div style={{ height: `${Math.round((m.internal / max) * 100)}%`, background: 'var(--muted)' }} />
          </div>
        ))}
      </div>
      <div style={axis}><span>{monthLabel(months[0].month)}</span><span>{monthLabel(months[months.length - 1].month)}</span></div>
    </div>
  );
}

function Tile({ label, value, sub }) {
  return (
    <div style={tile}>
      <div style={lbl}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, lineHeight: 1.2 }}>{value}</div>
      {sub ? <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}

// Single column on a phone; the tiles are the only grid and they wrap rather than squish.
const headerRow = { display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' };
const h3 = { display: 'flex', alignItems: 'center', gap: 7, fontSize: 15, fontWeight: 700, margin: 0 };
const hint = { fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, margin: '6px 0 0' };
const lbl = { fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--muted)' };
const box = { border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px', marginTop: 10, background: 'var(--card)' };
const tiles = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginTop: 10 };
const tile = { border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px', background: 'var(--card)' };
const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', minHeight: 40, border: '1.5px solid var(--hairline)', borderRadius: 8, fontSize: 13, outline: 'none', background: 'var(--card)', color: 'var(--text)' };
const mini = { padding: '10px 12px', minHeight: 40, borderRadius: 8, border: '1.5px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const dangerMini = { ...mini, color: 'var(--danger,#dc2626)', borderColor: 'var(--danger,#dc2626)' };
const chip = { ...mini, padding: '9px 12px' };
const chipOn = { borderColor: 'var(--brand)', color: 'var(--brand)', fontWeight: 800 };
const pickRow = { display: 'flex', gap: 10, alignItems: 'center', border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px', marginTop: 6, flexWrap: 'wrap' };
const tag = { marginLeft: 6, fontSize: 10.5, fontWeight: 800, color: '#fff', background: 'var(--brand)', borderRadius: 999, padding: '2px 7px', verticalAlign: 'middle' };
const scrollX = { overflowX: 'auto', marginTop: 8, WebkitOverflowScrolling: 'touch' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 460 };
const th = { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--hairline)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--muted)', whiteSpace: 'nowrap' };
const thNum = { ...th, textAlign: 'right' };
const td = { padding: '7px 8px', borderBottom: '1px solid var(--hairline)', verticalAlign: 'top' };
const tdNum = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
// The whole row is the control — a 40px tap target that spans the width, rather
// than a small button on the right that is easy to miss on a phone.
const convBtn = { display: 'flex', gap: 8, alignItems: 'flex-start', width: '100%', minHeight: 44, padding: '10px 0', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', color: 'var(--text)', font: 'inherit' };
const caret = { flexShrink: 0, width: 14, color: 'var(--muted)', fontSize: 16, lineHeight: '18px', transition: 'transform .15s ease' };
const threadBox = { border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px', background: 'var(--bg, var(--card))' };
const step = { padding: '8px 0', borderTop: '1px solid var(--hairline)' };
const pill = { fontSize: 10.5, fontWeight: 800, color: '#fff', borderRadius: 999, padding: '2px 7px' };
const retryBtn = { background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--brand)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' };
const stepLink = { display: 'inline-block', marginTop: 5, fontSize: 12, fontWeight: 600, color: 'var(--brand)', textDecoration: 'none' };
const axis = { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)', marginTop: 4 };
const key1 = { display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: 'var(--muted)', marginRight: 3 };
const key2 = { display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: 'var(--brand)', marginRight: 3, marginLeft: 6 };
